import React, { useState } from "react";
import { boardTemplateService, BoardTemplate, TemplateType, TEMPLATE_TYPES } from "../../../../lib/api/boardTemplates";
import { useAlertStore } from "../../../../stores/alertStore";
import { useTranslation } from "react-i18next";
import { convertGridItemsToStorage } from "../charts/chartStorage";

interface TemplateSaveFormProps {
    onSave: (template: BoardTemplate) => void;
    onCancel: () => void;
    chartConfig: any[];
}

export const TemplateSaveForm: React.FC<TemplateSaveFormProps> = ({
    onSave,
    onCancel,
    chartConfig,
}) => {
    const { t } = useTranslation("common");
    const { showAlert } = useAlertStore();
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [templateType, setTemplateType] = useState<TemplateType>("Other");
    const [isPublic, setIsPublic] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Map template type to translation key
    const getTypeKey = (type: string) => {
        const mapping: Record<string, string> = {
            'Financial Insights': 'financialInsights',
            'Decision Support': 'decisionSupport',
            'Operational Efficiency': 'operationalEfficiency',
            'Customer Behavior': 'customerBehavior',
            'Predictive Analytics': 'predictiveAnalytics',
            'Risk Management': 'riskManagement',
            'Other': 'other'
        };
        return mapping[type] || 'other';
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) return;

        setIsSubmitting(true);
        try {
            // Convert runtime GridItems to StoredChartConfig
            const storedConfig = convertGridItemsToStorage(chartConfig);

            const template = await boardTemplateService.createTemplate({
                name,
                description,
                templateType,
                chartConfig: storedConfig,
                isPublic,
            });
            onSave(template);
        } catch (error) {
            console.error("Error creating template:", error);
            showAlert({
                type: "error",
                title: String(t("errors.error")),
                message: String(t("userErrors.boards.unableToUpdate")),
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="flex flex-col h-full bg-white dark:bg-neutral-900">
            <div className="flex-1 overflow-y-auto p-6">
                <form id="save-template-form" onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="templateName" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                            {t("boards.boardName")}
                        </label>
                        <input
                            type="text"
                            id="templateName"
                            name="template-name-field"
                            autoComplete="off"
                            data-1p-ignore
                            data-lpignore="true"
                            data-bwignore="true"
                            data-form-type="other"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 rounded-lg focus:ring-accent-primary focus:border-accent-primary transition-colors"
                            placeholder={String(t("boards.boardName"))}
                            required
                            autoFocus
                        />
                    </div>

                    <div>
                        <label htmlFor="templateDescription" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                            {t("boards.boardDescription")}
                        </label>
                        <textarea
                            id="templateDescription"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 rounded-lg focus:ring-accent-primary focus:border-accent-primary transition-colors"
                            placeholder={String(t("boards.boardDescription"))}
                        />
                    </div>

                    <div>
                        <label htmlFor="templateType" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                            {t("templates.templateType")}
                        </label>
                        <select
                            id="templateType"
                            value={templateType}
                            onChange={(e) => setTemplateType(e.target.value as TemplateType)}
                            className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-lg focus:ring-accent-primary focus:border-accent-primary transition-colors bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                        >
                            {TEMPLATE_TYPES.map((type) => (
                                <option key={type} value={type}>
                                    {t(`templates.categories.${getTypeKey(type)}`)}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="isPublic"
                            checked={isPublic}
                            onChange={(e) => setIsPublic(e.target.checked)}
                            className="h-4 w-4 text-accent-primary focus:ring-accent-primary border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 rounded cursor-pointer"
                        />
                        <label htmlFor="isPublic" className="ml-2 block text-sm text-neutral-700 dark:text-neutral-300 cursor-pointer select-none">
                            {t("profile.public")}
                        </label>
                    </div>
                </form>
            </div>

            <div className="p-4 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 flex justify-end gap-3">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                >
                    {t("actions.cancel")}
                </button>
                <button
                    type="submit"
                    form="save-template-form"
                    disabled={isSubmitting || !name.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-accent-primary rounded-lg hover:bg-accent-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {isSubmitting ? t("actions.loading") : t("actions.save")}
                </button>
            </div>
        </div>
    );
};
